import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {
  WeatherFormValues,
  WeatherResponse,
} from '../../models/weather/weather.model';

dayjs.extend(utc);

interface WeatherState {
  error: string | null;
  formValues: WeatherFormValues;
  loading: boolean;
  results: WeatherResponse | null;
  success: boolean;
}

const getDefaultDateRange = () => {
  const endDate = dayjs().utc();
  const startDate = endDate.subtract(24, 'hour');

  return {
    startDate: startDate.toISOString().split('.')[0],
    endDate: endDate.toISOString().split('.')[0],
  };
};

export const INITIAL_WEATHER_STATE: WeatherState = {
  error: null,
  formValues: {
    location: {
      latitude: '',
      longitude: '',
    },
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
  },
  loading: false,
  results: null,
  success: false,
};

const WeatherSlice = createSlice({
  name: 'weather',
  initialState: INITIAL_WEATHER_STATE,
  reducers: {
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.success = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.error = null;
      state.success = false;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.success = action.payload;
      state.error = null;
      state.loading = false;
    },
    setWeatherFormValues: (state, action: PayloadAction<WeatherFormValues>) => {
      state.formValues = action.payload;
    },
    setWeatherResults: (
      state,
      action: PayloadAction<WeatherResponse | null>
    ) => {
      state.results = action.payload;
    },
  },
});

export const {
  setWeatherFormValues,
  setWeatherResults,
  setError,
  setLoading,
  setSuccess,
} = WeatherSlice.actions;

export default WeatherSlice.reducer;
